import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPost, apiPut } from '../lib/api.ts'
import { useSelectedCampaign } from '../lib/campaign.tsx'
import {
  cutWalkList,
  message,
  MISSING_LABEL,
  SIDE_LABEL,
  STATE_LABEL,
  ui,
  type CanvassDetail,
  type CanvassGroup,
  type Side,
} from '../canvass/model.ts'

/**
 * New canvass, and one canvass (decisions.md 2026-10-25: the old NextGen
 * "Create Campaign" wizard). A draft walks the five steps — Basics, Who,
 * Walkers, Questions, Preview & publish — in order, each one a page as the old
 * one was. A published canvass shows the same pieces as one page to run it
 * from: change walkers, the questions, the details; deactivate.
 *
 * Every rule is the database's (0104); the screen offers what will pass and
 * shows the database's sentence when something does not.
 */

const CanvassMap = lazy(() => import('../canvass/CanvassMap.tsx').then((m) => ({ default: m.CanvassMap })))

const STEPS = [
  ['basics', 'Basics'],
  ['who', 'Who to walk'],
  ['walkers', 'Walkers'],
  ['questions', 'Questions'],
  ['publish', 'Preview & publish'],
] as const
export type Step = (typeof STEPS)[number][0]

interface Script {
  script_id: string
  title: string
  body: string
  canvasses: number
}

interface Survey {
  survey_id: string
  title: string
  latest_published: number | null
  latest_published_version_id: string | null
}

interface Walker {
  user_id: string
  username: string
  display_name: string | null
  has_password: boolean
  status: string
}

function Alert({ text }: { text: string | null }) {
  return text ? (
    <p role="alert" className="mt-4 rounded-md border border-line bg-surface-sunken px-3 py-2 text-sm">
      {text}
    </p>
  ) : null
}

// ---------------------------------------------------------------------------
// New canvass: step 1 only, then the canvass page takes over.
// ---------------------------------------------------------------------------
export function CanvassNew() {
  const { campaignId, campaign } = useSelectedCampaign()
  const navigate = useNavigate()
  if (!campaign) return <p className="text-sm text-ink-muted">Loading…</p>
  if (!campaign.can_manage_canvassers) {
    return <p className="text-sm text-ink-muted">Only the Campaign Manager can start a canvass on this campaign.</p>
  }
  return (
    <div>
      <Header title="New canvass" />
      <StepBar current="basics" reachable={['basics']} />
      <BasicsForm
        campaignId={campaignId}
        electionDate={campaign.election_date}
        onSaved={(id) => void navigate({ to: '/canvasses/$canvassId', params: { canvassId: id }, search: { step: 'who' } })}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// One canvass.
// ---------------------------------------------------------------------------
export function CanvassPage({ canvassId, step }: { canvassId: string; step?: Step }) {
  const { campaignId, campaign } = useSelectedCampaign()
  const navigate = useNavigate()

  const detail = useQuery({
    queryKey: ['canvass', canvassId],
    queryFn: () => apiGet<CanvassDetail>(`/campaigns/${campaignId}/canvasses/${canvassId}`),
    enabled: Boolean(campaignId) && campaign?.can_manage_canvassers === true,
    // While the walk list is being made, watch for it to finish.
    refetchInterval: (q) => {
      const s = q.state.data?.list?.status
      return s === 'queued' || s === 'running' ? 2000 : false
    },
  })

  if (!campaign) return <p className="text-sm text-ink-muted">Loading…</p>
  if (!campaign.can_manage_canvassers) {
    return <p className="text-sm text-ink-muted">Only the Campaign Manager can run canvasses on this campaign.</p>
  }
  if (detail.isError) {
    return (
      <div>
        <Header title="Canvass" />
        <Alert text="This canvass could not be found on this campaign." />
      </div>
    )
  }
  const d = detail.data
  if (!d) return <p className="text-sm text-ink-muted">Loading…</p>

  const draft = d.state === 'draft' || d.state === 'ready'
  const go = (s: Step) => void navigate({ to: '/canvasses/$canvassId', params: { canvassId }, search: { step: s } })

  if (!draft) return <RunCanvass d={d} campaignId={campaignId} />

  const listReady = d.list?.status === 'complete'
  const reachable: Step[] = ['basics', 'who', ...(listReady ? (['walkers', 'questions', 'publish'] as Step[]) : [])]
  const current: Step = step && reachable.includes(step) ? step : !d.list ? 'who' : listReady && d.groups.length === 0 ? 'walkers' : step ?? 'who'

  return (
    <div>
      <Header title={d.name} sub={`${STATE_LABEL[d.state]} · ends ${d.effective_end_date ?? '—'}`} />
      <StepBar current={current} reachable={reachable} onGo={go} />
      {current === 'basics' ? (
        <BasicsForm campaignId={campaignId} electionDate={campaign.election_date} d={d} onSaved={() => go('who')} />
      ) : current === 'who' ? (
        <WhoStep d={d} campaignId={campaignId} onNext={() => go('walkers')} />
      ) : current === 'walkers' ? (
        <WalkersStep d={d} campaignId={campaignId} onNext={() => go('questions')} />
      ) : current === 'questions' ? (
        <QuestionsStep d={d} campaignId={campaignId} onNext={() => go('publish')} />
      ) : (
        <PublishStep d={d} campaignId={campaignId} onGo={go} />
      )}
    </div>
  )
}

function Header({ title, sub }: { title: string; sub?: string }) {
  return (
    <div>
      <Link to="/canvasses" className="text-xs font-medium text-brand-700 hover:underline">
        ‹ Canvasses
      </Link>
      <h1 className="mt-1 text-xl font-semibold tracking-tight">{title}</h1>
      {sub ? <p className="mt-0.5 text-sm text-ink-muted">{sub}</p> : null}
    </div>
  )
}

function StepBar({ current, reachable, onGo }: { current: Step; reachable: Step[]; onGo?: (s: Step) => void }) {
  return (
    <ol className="mt-5 flex flex-wrap gap-x-1 gap-y-2 text-sm" aria-label="Steps">
      {STEPS.map(([key, labelText], i) => {
        const here = key === current
        const open = reachable.includes(key) && onGo && !here
        return (
          <li key={key} className="flex items-center gap-1">
            {i > 0 ? <span aria-hidden className="px-1 text-ink-muted">›</span> : null}
            <button
              type="button"
              disabled={!open}
              aria-current={here ? 'step' : undefined}
              onClick={() => onGo?.(key)}
              className={
                here
                  ? 'rounded-md bg-brand-900 px-2.5 py-1 font-semibold text-white'
                  : open
                    ? 'rounded-md px-2.5 py-1 text-brand-800 hover:bg-surface-sunken'
                    : 'rounded-md px-2.5 py-1 text-ink-muted'
              }
            >
              <span className="tabular-nums">{i + 1}</span> {labelText}
            </button>
          </li>
        )
      })}
    </ol>
  )
}

// ---------------------------------------------------------------------------
// Step 1: Basics.
// ---------------------------------------------------------------------------
function BasicsForm({
  campaignId,
  electionDate,
  d,
  onSaved,
}: {
  campaignId: string
  electionDate: string | null
  d?: CanvassDetail
  onSaved: (canvassId: string) => void
}) {
  const client = useQueryClient()
  const [name, setName] = useState(d?.name ?? '')
  const [description, setDescription] = useState(d?.description ?? '')
  const [target, setTarget] = useState(d?.target_contacts ? String(d.target_contacts) : '')
  const [endDate, setEndDate] = useState(d?.end_date ?? '')
  const [picked, setPicked] = useState<string[]>(d?.scripts.map((s) => s.script_id) ?? [])
  const [error, setError] = useState<string | null>(null)

  const scripts = useQuery({
    queryKey: ['walker-scripts', campaignId],
    queryFn: () => apiGet<{ scripts: Script[] }>(`/campaigns/${campaignId}/walker-scripts`),
    enabled: Boolean(campaignId),
  })

  const save = useMutation({
    mutationFn: async () => {
      const basics = {
        name: name.trim(),
        description: description.trim() || null,
        target_contacts: target ? Number(target) : null,
        end_date: endDate || null,
      }
      let id = d?.canvass_id
      if (id) {
        await apiPut(`/campaigns/${campaignId}/canvasses/${id}`, {
          ...basics,
          survey_version_id: d?.survey?.survey_version_id ?? null,
        })
      } else {
        id = (await apiPost<{ canvass_id: string }>(`/campaigns/${campaignId}/canvasses`, basics)).canvass_id
      }
      await apiPut(`/campaigns/${campaignId}/canvasses/${id}/scripts`, { script_ids: picked })
      return id
    },
    onSuccess: async (id) => {
      setError(null)
      await client.invalidateQueries({ queryKey: ['canvass', id] })
      await client.invalidateQueries({ queryKey: ['canvasses', campaignId] })
      onSaved(id)
    },
    onError: (caught) => setError(message(caught)),
  })

  return (
    <form
      className="mt-6 max-w-2xl space-y-5"
      onSubmit={(e) => {
        e.preventDefault()
        if (name.trim()) save.mutate()
      }}
    >
      <label className="block text-sm">
        <span className={ui.label}>Canvass name</span>
        <input id="cv-name" value={name} onChange={(e) => setName(e.target.value)} required maxLength={200}
          placeholder="e.g. Saturday walk, west side" className={`${ui.input} w-full`} />
      </label>
      <label className="block text-sm">
        <span className={ui.label}>Description (optional)</span>
        <textarea id="cv-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={2}
          maxLength={4000} className={`${ui.input} w-full`} />
      </label>
      <div className="flex flex-wrap gap-4">
        <label className="text-sm">
          <span className={ui.label}>Target number of contacts (optional)</span>
          <input id="cv-target" type="number" min={1} value={target} onChange={(e) => setTarget(e.target.value)}
            className={`${ui.input} w-40`} />
        </label>
        <label className="text-sm">
          <span className={ui.label}>End date (optional)</span>
          <input id="cv-end" type="date" value={endDate} max={electionDate ?? undefined}
            onChange={(e) => setEndDate(e.target.value)} className={`${ui.input} w-44`} />
          <span className="mt-1 block text-xs text-ink-muted">
            {electionDate ? `Blank means election day, ${electionDate}.` : 'Blank means no end date.'}
          </span>
        </label>
      </div>

      <fieldset>
        <legend className={ui.label}>Walker scripts</legend>
        <p className="text-xs text-ink-muted">What walkers read at the door. They see each one as a tab in the app.</p>
        <div className="mt-2 space-y-1.5">
          {(scripts.data?.scripts ?? []).map((s) => (
            <label key={s.script_id} className="flex items-start gap-2 text-sm">
              <input
                id={`cv-script-${s.script_id}`}
                type="checkbox"
                className="mt-1"
                checked={picked.includes(s.script_id)}
                onChange={(e) =>
                  setPicked(e.target.checked ? [...picked, s.script_id] : picked.filter((x) => x !== s.script_id))
                }
              />
              <span>
                <span className="font-medium">{s.title}</span>
                <span className="block text-xs text-ink-muted line-clamp-1">{s.body}</span>
              </span>
            </label>
          ))}
          {scripts.data && scripts.data.scripts.length === 0 ? (
            <p className="text-sm text-ink-muted">No scripts yet.</p>
          ) : null}
        </div>
        <NewScript campaignId={campaignId} onCreated={(id) => setPicked((p) => [...p, id])} />
      </fieldset>

      <Alert text={error} />
      <div className="flex gap-2">
        <button type="submit" className={ui.primary} disabled={!name.trim() || save.isPending}>
          {save.isPending ? 'Saving…' : d ? 'Save and continue' : 'Next: who to walk'}
        </button>
      </div>
    </form>
  )
}

/** The old wizard's "Create Walker Script" button, inline. */
function NewScript({ campaignId, onCreated }: { campaignId: string; onCreated: (id: string) => void }) {
  const client = useQueryClient()
  const [open, setOpen] = useState(false)
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [error, setError] = useState<string | null>(null)
  const create = useMutation({
    mutationFn: () =>
      apiPost<{ script_id: string }>(`/campaigns/${campaignId}/walker-scripts`, { title: title.trim(), body }),
    onSuccess: async (r) => {
      await client.invalidateQueries({ queryKey: ['walker-scripts', campaignId] })
      onCreated(r.script_id)
      setOpen(false)
      setTitle('')
      setBody('')
      setError(null)
    },
    onError: (caught) => setError(message(caught)),
  })
  if (!open) {
    return (
      <button type="button" className={`${ui.secondary} mt-3`} onClick={() => setOpen(true)}>
        New script
      </button>
    )
  }
  return (
    <div className="mt-3 space-y-2 rounded-md border border-line bg-surface-sunken p-3">
      <input id="ns-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title"
        maxLength={200} className={`${ui.input} w-full`} aria-label="Script title" />
      <textarea id="ns-body" value={body} onChange={(e) => setBody(e.target.value)} rows={5} maxLength={20000}
        placeholder="Hi, my name is … I'm a volunteer with …" className={`${ui.input} w-full`} aria-label="Script text" />
      <Alert text={error} />
      <div className="flex gap-2">
        <button type="button" className={ui.secondary} disabled={!title.trim() || create.isPending}
          onClick={() => create.mutate()}>
          Save script
        </button>
        <button type="button" className="text-xs text-ink-muted hover:text-ink" onClick={() => setOpen(false)}>
          Cancel
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Step 2: Who to walk.
// ---------------------------------------------------------------------------
type Cmp = '>' | '<' | '='
interface PrecinctRow {
  precinct_id: string
  city: string | null
  doors: number
  voters: number
}

function passes(n: number, cmp: Cmp, v: string): boolean {
  if (v === '') return true
  const x = Number(v)
  return cmp === '>' ? n > x : cmp === '<' ? n < x : n === x
}

function WhoStep({ d, campaignId, onNext }: { d: CanvassDetail; campaignId: string; onNext: () => void }) {
  const client = useQueryClient()
  const [tab, setTab] = useState<'precincts' | 'universe' | 'area'>(
    d.who.kind === 'universe' ? 'universe' : d.who.kind === 'area' ? 'area' : 'precincts',
  )
  const [error, setError] = useState<string | null>(null)
  const [replacing, setReplacing] = useState(!d.list)

  const attach = useMutation({
    mutationFn: async (args: {
      who: Record<string, unknown>
      universe: { name: string; description: string; ast: unknown } | { universe_id: string }
    }) => {
      const exportId = await cutWalkList(campaignId, d.name, args.universe)
      await apiPut(`/campaigns/${campaignId}/canvasses/${d.canvass_id}/list`, { export_id: exportId, who: args.who })
    },
    onSuccess: async () => {
      setError(null)
      setReplacing(false)
      await client.invalidateQueries({ queryKey: ['canvass', d.canvass_id] })
    },
    onError: (caught) => setError(message(caught)),
  })

  const status = d.list?.status
  return (
    <div className="mt-6">
      {d.list && !replacing ? (
        <section className="rounded-lg border border-line p-4">
          <h2 className="text-sm font-semibold">Who this canvass walks</h2>
          <p className="mt-1 text-sm">{describeWho(d)}</p>
          {status === 'queued' || status === 'running' ? (
            <p className="mt-2 text-sm text-ink-muted" role="status">Making the walk list… this takes a few seconds.</p>
          ) : status === 'complete' ? (
            <p className="mt-2 text-sm tabular-nums">
              {d.totals.precincts} precinct{d.totals.precincts === 1 ? '' : 's'} ·{' '}
              {d.totals.doors.toLocaleString()} houses · {d.totals.voters.toLocaleString()} voters
            </p>
          ) : (
            <p className="mt-2 text-sm">{MISSING_LABEL[`list_${status}`] ?? 'The walk list is not usable.'}</p>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            {status === 'complete' ? (
              <button type="button" className={ui.primary} onClick={onNext}>
                Next: walkers
              </button>
            ) : null}
            <button type="button" className={ui.secondary} onClick={() => setReplacing(true)}>
              Choose again
            </button>
          </div>
          {d.groups.length > 0 ? (
            <p className="mt-2 text-xs text-ink-muted">Choosing again clears the groups and walkers already set.</p>
          ) : null}
        </section>
      ) : (
        <>
          <div role="tablist" className="flex flex-wrap gap-1 border-b border-line">
            {(
              [
                ['precincts', 'Pick precincts'],
                ['universe', 'Use a saved universe'],
                ['area', 'An area on the map'],
              ] as const
            ).map(([k, l]) => (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={tab === k}
                onClick={() => setTab(k)}
                className={
                  tab === k
                    ? '-mb-px border-b-2 border-brand-700 px-3 py-2 text-sm font-semibold'
                    : 'px-3 py-2 text-sm text-ink-muted hover:text-ink'
                }
              >
                {l}
              </button>
            ))}
          </div>
          <div className="mt-4">
            {tab === 'precincts' ? (
              <PrecinctPicker
                campaignId={campaignId}
                initial={d.who.precincts ?? []}
                busy={attach.isPending}
                onUse={(ids) =>
                  attach.mutate({
                    who: { kind: 'precincts', precincts: ids },
                    universe: {
                      name: `Canvass ${d.name} — precinct${ids.length === 1 ? '' : 's'}`,
                      description: `Everyone in precinct${ids.length === 1 ? '' : 's'} ${ids.join(', ')}.`,
                      ast: { op: 'field', field: 'precinct_id', cmp: 'in', value: ids },
                    },
                  })
                }
              />
            ) : tab === 'universe' ? (
              <UniversePicker
                campaignId={campaignId}
                busy={attach.isPending}
                onUse={(u) =>
                  attach.mutate({ who: { kind: 'universe', universe_name: u.name }, universe: { universe_id: u.universe_id } })
                }
              />
            ) : (
              <AreaPicker
                campaignId={campaignId}
                busy={attach.isPending}
                onUse={(t) =>
                  attach.mutate({
                    who: { kind: 'area', turf_id: t.turf_id, turf_name: t.name },
                    universe: {
                      name: `Canvass ${d.name} — ${t.name}`,
                      description: `Everyone inside the area “${t.name}”.`,
                      ast: { op: 'turf', turf_id: t.turf_id },
                    },
                  })
                }
              />
            )}
          </div>
          {d.list ? (
            <button type="button" className="mt-3 text-xs text-ink-muted hover:text-ink" onClick={() => setReplacing(false)}>
              Keep the current choice
            </button>
          ) : null}
        </>
      )}
      <Alert text={error} />
    </div>
  )
}

function describeWho(d: CanvassDetail): string {
  const w = d.who
  if (w.kind === 'precincts' && w.precincts) {
    const p = w.precincts
    return `Precinct${p.length === 1 ? '' : 's'} ${p.length <= 8 ? p.join(', ') : `${p.slice(0, 8).join(', ')} and ${p.length - 8} more`}`
  }
  if (w.kind === 'area') return `Everyone inside the area “${w.turf_name ?? 'drawn on the map'}”`
  if (w.kind === 'universe') return `The universe “${w.universe_name ?? 'saved universe'}”`
  return `Walk list #${d.list?.export_no ?? ''}`
}

/** The old precinct table: houses and voters per precinct, filters, checkboxes. */
function PrecinctPicker({
  campaignId,
  initial,
  busy,
  onUse,
}: {
  campaignId: string
  initial: string[]
  busy: boolean
  onUse: (ids: string[]) => void
}) {
  const [q, setQ] = useState('')
  const [search, setSearch] = useState('')
  const [houseCmp, setHouseCmp] = useState<Cmp>('>')
  const [houseN, setHouseN] = useState('')
  const [voterCmp, setVoterCmp] = useState<Cmp>('>')
  const [voterN, setVoterN] = useState('')
  const [picked, setPicked] = useState<Record<string, PrecinctRow>>({})
  const seeded = useRef(false)

  const rows = useQuery({
    queryKey: ['precinct-table', campaignId, search],
    queryFn: () =>
      apiGet<{ precincts: PrecinctRow[] }>(`/campaigns/${campaignId}/precinct-table?q=${encodeURIComponent(search)}`),
    enabled: Boolean(search),
  })

  // A canvass coming back to this step starts with its own precincts ticked.
  useEffect(() => {
    if (seeded.current || initial.length === 0) return
    seeded.current = true
    setSearch(initial[0]!.slice(0, 4))
    setQ(initial[0]!.slice(0, 4))
  }, [initial])
  useEffect(() => {
    if (!rows.data || initial.length === 0) return
    setPicked((p) => {
      if (Object.keys(p).length > 0) return p
      const next: Record<string, PrecinctRow> = {}
      for (const r of rows.data.precincts) if (initial.includes(r.precinct_id)) next[r.precinct_id] = r
      return next
    })
  }, [rows.data, initial])

  const shown = (rows.data?.precincts ?? []).filter(
    (r) => passes(r.doors, houseCmp, houseN) && passes(r.voters, voterCmp, voterN),
  )
  const pickedRows = Object.values(picked)
  const allShownPicked = shown.length > 0 && shown.every((r) => picked[r.precinct_id])

  return (
    <div>
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault()
          setSearch(q.trim())
        }}
      >
        <label className="text-sm">
          <span className={ui.label}>City or precinct number</span>
          <input id="pp-q" value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. Riverside or 1900"
            className={`${ui.input} w-64`} />
        </label>
        <button type="submit" className={ui.secondary} disabled={!q.trim()}>Search</button>
        <label className="text-sm">
          <span className={ui.label}>Houses</span>
          <span className="flex gap-1">
            <select id="pp-hc" value={houseCmp} onChange={(e) => setHouseCmp(e.target.value as Cmp)} className={ui.input} aria-label="Houses comparison">
              <option value=">">more than</option><option value="<">fewer than</option><option value="=">exactly</option>
            </select>
            <input id="pp-hn" type="number" min={0} value={houseN} onChange={(e) => setHouseN(e.target.value)}
              className={`${ui.input} w-20`} aria-label="Houses" />
          </span>
        </label>
        <label className="text-sm">
          <span className={ui.label}>Voters</span>
          <span className="flex gap-1">
            <select id="pp-vc" value={voterCmp} onChange={(e) => setVoterCmp(e.target.value as Cmp)} className={ui.input} aria-label="Voters comparison">
              <option value=">">more than</option><option value="<">fewer than</option><option value="=">exactly</option>
            </select>
            <input id="pp-vn" type="number" min={0} value={voterN} onChange={(e) => setVoterN(e.target.value)}
              className={`${ui.input} w-20`} aria-label="Voters" />
          </span>
        </label>
      </form>

      {rows.isFetching ? <p className="mt-3 text-sm text-ink-muted">Searching…</p> : null}
      {rows.data && rows.data.precincts.length === 0 ? (
        <p className="mt-3 text-sm text-ink-muted">No precincts match “{search}”.</p>
      ) : null}
      {shown.length > 0 ? (
        <div className="mt-3 max-h-96 overflow-auto rounded-lg border border-line">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-surface-sunken text-left text-xs tracking-wide text-ink-muted uppercase">
              <tr>
                <th className="w-10 px-3 py-2">
                  <input
                    id="pp-all"
                    type="checkbox"
                    aria-label="Pick every precinct shown"
                    checked={allShownPicked}
                    onChange={(e) =>
                      setPicked((p) => {
                        const next = { ...p }
                        for (const r of shown) {
                          if (e.target.checked) next[r.precinct_id] = r
                          else delete next[r.precinct_id]
                        }
                        return next
                      })
                    }
                  />
                </th>
                <th className="px-3 py-2 font-medium">Precinct</th>
                <th className="px-3 py-2 font-medium">City</th>
                <th className="px-3 py-2 text-right font-medium">Houses</th>
                <th className="px-3 py-2 text-right font-medium">Voters</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line tabular-nums">
              {shown.map((r) => (
                <tr key={r.precinct_id}>
                  <td className="px-3 py-1.5">
                    <input
                      id={`pp-${r.precinct_id}`}
                      type="checkbox"
                      aria-label={`Precinct ${r.precinct_id}`}
                      checked={Boolean(picked[r.precinct_id])}
                      onChange={(e) =>
                        setPicked((p) => {
                          const next = { ...p }
                          if (e.target.checked) next[r.precinct_id] = r
                          else delete next[r.precinct_id]
                          return next
                        })
                      }
                    />
                  </td>
                  <td className="px-3 py-1.5 font-medium">{r.precinct_id}</td>
                  <td className="px-3 py-1.5">{r.city ?? '—'}</td>
                  <td className="px-3 py-1.5 text-right">{r.doors.toLocaleString()}</td>
                  <td className="px-3 py-1.5 text-right">{r.voters.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button type="button" className={ui.primary} disabled={pickedRows.length === 0 || busy}
          onClick={() => onUse(pickedRows.map((r) => r.precinct_id).sort())}>
          {busy ? 'Making the walk list…' : `Walk ${pickedRows.length || ''} precinct${pickedRows.length === 1 ? '' : 's'}`}
        </button>
        {pickedRows.length > 0 ? (
          <span className="text-sm tabular-nums text-ink-muted">
            {pickedRows.reduce((a, r) => a + r.doors, 0).toLocaleString()} houses ·{' '}
            {pickedRows.reduce((a, r) => a + r.voters, 0).toLocaleString()} voters
          </span>
        ) : null}
      </div>
      <p className="mt-2 max-w-prose text-xs text-ink-muted">
        Everyone registered in these precincts, with their whole household. To walk only some of
        them (say, undecided voters), build a universe on the Lists side and use it here instead.
      </p>
    </div>
  )
}

function UniversePicker({
  campaignId,
  busy,
  onUse,
}: {
  campaignId: string
  busy: boolean
  onUse: (u: { universe_id: string; name: string }) => void
}) {
  const [id, setId] = useState('')
  const list = useQuery({
    queryKey: ['universes', campaignId],
    queryFn: () =>
      apiGet<{ universes: { universe_id: string; name: string; row_count: string | number | null }[] }>(
        `/universes?campaign_id=${campaignId}`,
      ),
  })
  const universes = list.data?.universes ?? []
  const chosen = universes.find((u) => u.universe_id === id)
  return (
    <div className="space-y-3">
      <p className="max-w-prose text-sm text-ink-muted">
        A universe built on the Lists side — for example undecided voters in these precincts. The
        walk list includes each one's household.
      </p>
      <select id="up-u" value={id} onChange={(e) => setId(e.target.value)} className={`${ui.input} w-full max-w-md`}
        aria-label="Universe">
        <option value="">Choose a universe</option>
        {universes.map((u) => (
          <option key={u.universe_id} value={u.universe_id}>
            {u.name}
            {u.row_count != null ? ` — ${Number(u.row_count).toLocaleString()} voters` : ''}
          </option>
        ))}
      </select>
      <div className="flex flex-wrap gap-3">
        <button type="button" className={ui.primary} disabled={!chosen || busy} onClick={() => chosen && onUse(chosen)}>
          {busy ? 'Making the walk list…' : 'Walk this universe'}
        </button>
        <Link to="/universes/new" className={`${ui.secondary} self-center`}>Build a universe</Link>
      </div>
    </div>
  )
}

function AreaPicker({
  campaignId,
  busy,
  onUse,
}: {
  campaignId: string
  busy: boolean
  onUse: (t: { turf_id: string; name: string }) => void
}) {
  const [id, setId] = useState('')
  const list = useQuery({
    queryKey: ['turf', campaignId],
    queryFn: () =>
      apiGet<{ turf: { turf_id: string; name: string; doors: string; voters: string }[] }>(`/turf?campaign_id=${campaignId}`),
  })
  const areas = list.data?.turf ?? []
  const chosen = areas.find((t) => t.turf_id === id)
  return (
    <div className="space-y-3">
      <p className="max-w-prose text-sm text-ink-muted">
        An area drawn on the map. Draw a new one on the <Link to="/turf" className={ui.link}>Map areas</Link> page,
        then come back and pick it here.
      </p>
      <select id="ap-t" value={id} onChange={(e) => setId(e.target.value)} className={`${ui.input} w-full max-w-md`}
        aria-label="Area">
        <option value="">Choose an area</option>
        {areas.map((t) => (
          <option key={t.turf_id} value={t.turf_id}>
            {t.name} — {Number(t.doors).toLocaleString()} houses · {Number(t.voters).toLocaleString()} voters
          </option>
        ))}
      </select>
      <button type="button" className={ui.primary} disabled={!chosen || busy} onClick={() => chosen && onUse(chosen)}>
        {busy ? 'Making the walk list…' : 'Walk this area'}
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Step 3: Walkers — groups, each App or Paper, each walked by one or a pair.
// ---------------------------------------------------------------------------
function WalkersStep({ d, campaignId, onNext }: { d: CanvassDetail; campaignId: string; onNext?: () => void }) {
  const client = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [mapOpen, setMapOpen] = useState(false)
  const autoGrouped = useRef(false)

  const walkers = useQuery({
    queryKey: ['canvassers', campaignId],
    queryFn: () => apiGet<{ canvassers: Walker[] }>(`/campaigns/${campaignId}/canvassers`),
  })

  const refresh = () => client.invalidateQueries({ queryKey: ['canvass', d.canvass_id] })
  const groupByPrecinct = useMutation({
    mutationFn: () => apiPost(`/campaigns/${campaignId}/canvasses/${d.canvass_id}/group-by-precinct`),
    onSuccess: async () => {
      setError(null)
      await refresh()
    },
    onError: (caught) => setError(message(caught)),
  })

  // The old wizard listed the precincts straight away: one group each.
  useEffect(() => {
    if (autoGrouped.current || d.groups.length > 0 || d.list?.status !== 'complete') return
    autoGrouped.current = true
    groupByPrecinct.mutate()
  }, [d.groups.length, d.list?.status, groupByPrecinct])

  const people = (walkers.data?.canvassers ?? []).filter((w) => w.status === 'active')

  return (
    <div className="mt-6">
      <p className="max-w-prose text-sm text-ink-muted">
        Each group is walked with the app by one walker or a pair (a pair splits the street: one
        takes the even numbers, the other the odd), or on paper. One walker can take several groups.
      </p>
      <Alert text={error} />

      {d.groups.length === 0 ? (
        <p className="mt-4 text-sm text-ink-muted">{groupByPrecinct.isPending ? 'Making one group per precinct…' : 'No groups yet.'}</p>
      ) : (
        <ul className="mt-4 space-y-3">
          {d.groups.map((g) => (
            <GroupEditor key={g.group_id} g={g} d={d} campaignId={campaignId} people={people} onError={setError} />
          ))}
        </ul>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {d.totals.ungrouped_doors > 0 ? (
          <>
            <span className="text-sm">{d.totals.ungrouped_doors.toLocaleString()} houses are not in a group.</span>
            <button type="button" className={ui.secondary} disabled={groupByPrecinct.isPending}
              onClick={() => groupByPrecinct.mutate()}>
              Group them by precinct
            </button>
          </>
        ) : null}
        <button type="button" className={ui.secondary} onClick={() => setMapOpen((o) => !o)}>
          {mapOpen ? 'Close the map' : 'Split a group on the map'}
        </button>
      </div>

      {mapOpen ? (
        <Suspense fallback={<p className="mt-3 text-sm text-ink-muted">Loading the map…</p>}>
          <CanvassMap
            campaignId={campaignId}
            d={d}
            onCreated={async () => {
              await refresh()
            }}
            onError={setError}
          />
        </Suspense>
      ) : null}

      <NewWalker campaignId={campaignId} />

      {onNext ? (
        <div className="mt-6">
          <button type="button" className={ui.primary} onClick={onNext}>
            Next: questions
          </button>
        </div>
      ) : null}
    </div>
  )
}

type Row = { user_id: string; side: Side }

function defaultSides(rows: Row[]): Row[] {
  if (rows.length === 1) return [{ ...rows[0]!, side: 'all' }]
  if (rows.length === 2 && rows.every((r) => r.side === 'all')) {
    return [
      { ...rows[0]!, side: 'even' },
      { ...rows[1]!, side: 'odd' },
    ]
  }
  return rows
}

function GroupEditor({
  g,
  d,
  campaignId,
  people,
  onError,
}: {
  g: CanvassGroup
  d: CanvassDetail
  campaignId: string
  people: Walker[]
  onError: (e: string | null) => void
}) {
  const client = useQueryClient()
  const [mode, setMode] = useState(g.mode)
  const [rows, setRows] = useState<Row[]>(g.members.map((m) => ({ user_id: m.user_id, side: m.side })))
  const [paperWalker, setPaperWalker] = useState(g.paper_walker ?? '')
  const [name, setName] = useState(g.name)
  const [confirmRemove, setConfirmRemove] = useState(false)

  const original = JSON.stringify([g.mode, g.members.map((m) => [m.user_id, m.side]), g.paper_walker ?? '', g.name])
  const now = JSON.stringify([mode, rows.filter((r) => r.user_id).map((r) => [r.user_id, r.side]), mode === 'paper' ? paperWalker : '', name])
  const dirty = original !== now

  const save = useMutation({
    mutationFn: () =>
      apiPut(`/campaigns/${campaignId}/canvasses/${d.canvass_id}/groups/${g.group_id}`, {
        name: name.trim() || null,
        mode,
        members: mode === 'app' ? rows.filter((r) => r.user_id) : [],
        paper_walker: mode === 'paper' ? paperWalker.trim() || null : null,
      }),
    onSuccess: async () => {
      onError(null)
      await client.invalidateQueries({ queryKey: ['canvass', d.canvass_id] })
    },
    onError: (caught) => onError(message(caught)),
  })
  const remove = useMutation({
    mutationFn: () => apiPost(`/campaigns/${campaignId}/canvasses/${d.canvass_id}/groups/${g.group_id}/delete`),
    onSuccess: async () => {
      onError(null)
      await client.invalidateQueries({ queryKey: ['canvass', d.canvass_id] })
    },
    onError: (caught) => onError(message(caught)),
  })

  const nameOf = (id: string) => {
    const p = people.find((x) => x.user_id === id)
    return p ? p.display_name ?? p.username : 'Walker'
  }

  return (
    <li className="rounded-lg border border-line p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <input id={`g-name-${g.group_id}`} value={name} onChange={(e) => setName(e.target.value)} maxLength={200}
          aria-label="Group name" className="min-w-0 rounded border border-transparent bg-transparent px-1 font-semibold hover:border-line focus:border-line" />
        <span className="text-sm tabular-nums text-ink-muted">
          {g.doors.toLocaleString()} houses · {g.voters.toLocaleString()} voters
        </span>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
        <span role="radiogroup" aria-label="How this group is walked" className="flex overflow-hidden rounded-md border border-line">
          {(['app', 'paper'] as const).map((m) => (
            <button key={m} type="button" role="radio" aria-checked={mode === m} onClick={() => setMode(m)}
              className={mode === m ? 'bg-brand-900 px-3 py-1 text-xs font-semibold text-white' : 'px-3 py-1 text-xs text-ink-muted hover:text-ink'}>
              {m === 'app' ? 'App' : 'Paper'}
            </button>
          ))}
        </span>

        {mode === 'app' ? (
          <span className="flex flex-wrap items-center gap-2">
            {rows.map((r, i) => (
              <span key={i} className="flex items-center gap-1">
                <select
                  id={`g-w-${g.group_id}-${i}`}
                  value={r.user_id}
                  aria-label={`Walker ${i + 1}`}
                  onChange={(e) => setRows(defaultSides(rows.map((x, j) => (j === i ? { ...x, user_id: e.target.value } : x))))}
                  className={ui.input}
                >
                  <option value="">Choose a walker</option>
                  {people.map((p) => (
                    <option key={p.user_id} value={p.user_id}
                      disabled={!p.has_password || rows.some((x, j) => j !== i && x.user_id === p.user_id)}>
                      {p.display_name ?? p.username}
                      {!p.has_password ? ' (needs a new password)' : ''}
                    </option>
                  ))}
                </select>
                {rows.length === 2 ? (
                  <select id={`g-s-${g.group_id}-${i}`} value={r.side} aria-label={`Side for ${nameOf(r.user_id)}`}
                    onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, side: e.target.value as Side } : x)))}
                    className={ui.input}>
                    {(['even', 'odd', 'all'] as const).map((s) => (
                      <option key={s} value={s}>{SIDE_LABEL[s]}</option>
                    ))}
                  </select>
                ) : null}
                <button type="button" aria-label="Take this walker off" className="px-1 text-ink-muted hover:text-ink"
                  onClick={() => setRows(defaultSides(rows.filter((_, j) => j !== i)))}>
                  ×
                </button>
              </span>
            ))}
            {rows.length === 0 ? (
              <button type="button" className={ui.secondary} onClick={() => setRows([{ user_id: '', side: 'all' }])}>
                Assign a walker
              </button>
            ) : rows.length === 1 ? (
              <button type="button" className="text-xs text-brand-700 hover:underline"
                onClick={() => setRows(defaultSides([...rows, { user_id: '', side: 'all' }]))}>
                + Make it a pair
              </button>
            ) : null}
          </span>
        ) : (
          <label className="flex items-center gap-2">
            <span className="text-xs text-ink-muted">Walker’s name on the sheets</span>
            <input id={`g-p-${g.group_id}`} value={paperWalker} onChange={(e) => setPaperWalker(e.target.value)}
              maxLength={200} placeholder="optional" className={`${ui.input} w-48`} />
          </label>
        )}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        {dirty ? (
          <button type="button" className={ui.secondary} disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save group'}
          </button>
        ) : mode === 'app' && g.members.length === 0 ? (
          <span className="text-xs text-ink-muted">No walker yet</span>
        ) : null}
        {confirmRemove ? (
          <span className="flex items-center gap-2 text-xs">
            Its houses go back to "not in a group".
            <button type="button" className={ui.secondary} onClick={() => remove.mutate()}>Remove group</button>
            <button type="button" className="text-ink-muted hover:text-ink" onClick={() => setConfirmRemove(false)}>Keep</button>
          </span>
        ) : (
          <button type="button" className="ml-auto text-xs text-ink-muted hover:text-ink" onClick={() => setConfirmRemove(true)}>
            Remove group
          </button>
        )}
      </div>
    </li>
  )
}

/** The old wizard's "Create Walker", inline: an account for the walk app. */
function NewWalker({ campaignId }: { campaignId: string }) {
  const client = useQueryClient()
  const [open, setOpen] = useState(false)
  const [username, setUsername] = useState('')
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [done, setDone] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const create = useMutation({
    mutationFn: () =>
      apiPost<{ username: string }>(`/campaigns/${campaignId}/canvassers`, {
        username: username.trim(),
        password,
        ...(name.trim() ? { display_name: name.trim() } : {}),
        ...(phone.trim() ? { phone: phone.trim() } : {}),
      }),
    onSuccess: async (r) => {
      setDone(`${r.username} can sign in with the password you set and the campaign PIN (shown on the Walkers page).`)
      setUsername('')
      setName('')
      setPhone('')
      setPassword('')
      setError(null)
      await client.invalidateQueries({ queryKey: ['canvassers', campaignId] })
    },
    onError: (caught) => setError(message(caught)),
  })
  return (
    <div className="mt-6">
      {!open ? (
        <button type="button" className={ui.secondary} onClick={() => setOpen(true)}>Create a walker</button>
      ) : (
        <form
          className="rounded-lg border border-line bg-surface-sunken p-3"
          onSubmit={(e) => {
            e.preventDefault()
            create.mutate()
          }}
        >
          <h3 className="text-sm font-semibold">Create a walker</h3>
          <div className="mt-2 flex flex-wrap items-end gap-3">
            <label className="text-sm">
              <span className={ui.label}>Username</span>
              <input id="nw-user" value={username} onChange={(e) => setUsername(e.target.value)} autoCapitalize="none"
                autoCorrect="off" pattern="[A-Za-z0-9][A-Za-z0-9._\-]{2,31}" className={`${ui.input} w-40`} />
            </label>
            <label className="text-sm">
              <span className={ui.label}>Name</span>
              <input id="nw-name" value={name} onChange={(e) => setName(e.target.value)} className={`${ui.input} w-44`} />
            </label>
            <label className="text-sm">
              <span className={ui.label}>Phone</span>
              <input id="nw-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={32}
                className={`${ui.input} w-36`} />
            </label>
            <label className="text-sm">
              <span className={ui.label}>Password</span>
              <input id="nw-pw" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="off"
                autoCapitalize="none" spellCheck={false} minLength={8} placeholder="at least 8 characters"
                className={`${ui.input} w-44`} />
            </label>
            <button type="submit" className={ui.primary} disabled={username.trim().length < 3 || password.length < 8 || create.isPending}>
              Create
            </button>
            <button type="button" className="text-xs text-ink-muted hover:text-ink" onClick={() => setOpen(false)}>Close</button>
          </div>
          {done ? <p className="mt-2 text-sm" role="status">{done}</p> : null}
          <Alert text={error} />
        </form>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Step 4: Questions.
// ---------------------------------------------------------------------------
function QuestionsStep({ d, campaignId, onNext }: { d: CanvassDetail; campaignId: string; onNext?: () => void }) {
  const client = useQueryClient()
  const [surveyVersion, setSurveyVersion] = useState(d.survey?.survey_version_id ?? '')
  const [error, setError] = useState<string | null>(null)
  const surveys = useQuery({
    queryKey: ['surveys', campaignId],
    queryFn: () => apiGet<{ surveys: Survey[] }>(`/surveys?campaign_id=${campaignId}`),
  })
  const published = (surveys.data?.surveys ?? []).filter((s) => s.latest_published_version_id)

  const save = useMutation({
    mutationFn: () =>
      apiPut(`/campaigns/${campaignId}/canvasses/${d.canvass_id}`, {
        name: d.name,
        description: d.description,
        target_contacts: d.target_contacts,
        end_date: d.end_date,
        survey_version_id: surveyVersion || null,
      }),
    onSuccess: async () => {
      setError(null)
      await client.invalidateQueries({ queryKey: ['canvass', d.canvass_id] })
      onNext?.()
    },
    onError: (caught) => setError(message(caught)),
  })

  const changed = (d.survey?.survey_version_id ?? '') !== surveyVersion

  return (
    <div className="mt-6 max-w-2xl space-y-5">
      <section className="rounded-lg border border-line p-4">
        <h2 className="text-sm font-semibold">Asked at every door</h2>
        <p className="mt-2 text-base">Can we count on your support?</p>
        <p className="mt-1 flex gap-2 text-sm">
          {['Yes', 'No', 'Undecided'].map((a) => (
            <span key={a} className="rounded-full border border-line px-2.5 py-0.5">{a}</span>
          ))}
        </p>
        <p className="mt-2 text-xs text-ink-muted">
          Built in. Answers count toward Supporters and can be used in lists (“said Yes at the door”).
        </p>
      </section>

      <section>
        <h2 className="text-sm font-semibold">A deeper survey (optional)</h2>
        <p className="mt-1 text-sm text-ink-muted">Asked after the support question, with branching if the survey has it.</p>
        <select id="q-survey" value={surveyVersion} onChange={(e) => setSurveyVersion(e.target.value)}
          className={`${ui.input} mt-2 w-full max-w-md`} aria-label="Survey">
          <option value="">No survey — support question only</option>
          {published.map((s) => (
            <option key={s.survey_id} value={s.latest_published_version_id!}>
              {s.title} (version {s.latest_published})
            </option>
          ))}
          {d.survey && !published.some((s) => s.latest_published_version_id === d.survey!.survey_version_id) ? (
            <option value={d.survey.survey_version_id}>{d.survey.title} (version {d.survey.version})</option>
          ) : null}
        </select>
        <p className="mt-2 text-xs text-ink-muted">
          Build or copy a survey on the <Link to="/surveys" className={ui.link}>Surveys</Link> page and publish it to
          use it here.
        </p>
      </section>

      <Alert text={error} />
      <div className="flex gap-2">
        {onNext ? (
          <button type="button" className={ui.primary} disabled={save.isPending}
            onClick={() => (changed ? save.mutate() : onNext())}>
            Next: preview
          </button>
        ) : (
          <button type="button" className={ui.primary} disabled={!changed || save.isPending} onClick={() => save.mutate()}>
            Save questions
          </button>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Step 5: Preview & publish.
// ---------------------------------------------------------------------------
function PublishStep({ d, campaignId, onGo }: { d: CanvassDetail; campaignId: string; onGo: (s: Step) => void }) {
  const client = useQueryClient()
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const publish = useMutation({
    mutationFn: () => apiPost(`/campaigns/${campaignId}/canvasses/${d.canvass_id}/publish`),
    onSuccess: async () => {
      setError(null)
      await client.invalidateQueries({ queryKey: ['canvass', d.canvass_id] })
      await client.invalidateQueries({ queryKey: ['canvasses', campaignId] })
    },
    onError: (caught) => setError(message(caught)),
  })

  const byWalker = useMemo(() => {
    const m = new Map<string, { name: string; groups: CanvassGroup[] }>()
    for (const g of d.groups) {
      if (g.mode === 'paper') continue
      for (const w of g.members) {
        const e = m.get(w.user_id) ?? { name: w.display_name ?? w.username, groups: [] }
        e.groups.push(g)
        m.set(w.user_id, e)
      }
    }
    return [...m.values()]
  }, [d.groups])
  const paper = d.groups.filter((g) => g.mode === 'paper')

  return (
    <div className="mt-6 max-w-3xl space-y-5">
      <Summary d={d} onGo={onGo} />

      <section>
        <h2 className="text-sm font-semibold">Who walks what</h2>
        <ul className="mt-2 divide-y divide-line rounded-lg border border-line text-sm">
          {byWalker.map((w) => (
            <li key={w.name} className="flex flex-wrap justify-between gap-2 px-3 py-2">
              <span className="font-medium">{w.name}</span>
              <span className="text-ink-muted">
                {w.groups.map((g) => g.name).join(', ')} ·{' '}
                {w.groups.reduce((a, g) => a + g.doors, 0).toLocaleString()} houses
              </span>
            </li>
          ))}
          {paper.map((g) => (
            <li key={g.group_id} className="flex flex-wrap justify-between gap-2 px-3 py-2">
              <span className="font-medium">{g.paper_walker ?? 'Paper'} <span className="text-xs text-ink-muted">(paper)</span></span>
              <span className="text-ink-muted">{g.name} · {g.doors.toLocaleString()} houses</span>
            </li>
          ))}
          {byWalker.length === 0 && paper.length === 0 ? <li className="px-3 py-2 text-ink-muted">Nobody yet.</li> : null}
        </ul>
        {d.totals.ungrouped_doors > 0 ? (
          <p className="mt-2 text-xs text-ink-muted">
            {d.totals.ungrouped_doors.toLocaleString()} houses are in no group and will not be walked.
          </p>
        ) : null}
      </section>

      {d.missing.length > 0 ? (
        <section className="rounded-md border border-line bg-surface-sunken p-3 text-sm">
          <h2 className="font-semibold">Before you can publish</h2>
          <ul className="mt-1 list-disc pl-5">
            {d.missing.map((m) => (
              <li key={m}>{MISSING_LABEL[m] ?? m}</li>
            ))}
          </ul>
        </section>
      ) : null}

      <Alert text={error} />
      <div className="flex flex-wrap gap-2">
        <button type="button" className={ui.primary} disabled={d.missing.length > 0 || publish.isPending}
          onClick={() => publish.mutate()}>
          {publish.isPending ? 'Publishing…' : 'Save and publish'}
        </button>
        <button type="button" className={ui.secondary} onClick={() => void navigate({ to: '/canvasses' })}>
          Save for later
        </button>
      </div>
    </div>
  )
}

function Summary({ d, onGo }: { d: CanvassDetail; onGo?: (s: Step) => void }) {
  const edit = (s: Step) =>
    onGo ? (
      <button type="button" className="text-xs text-brand-700 hover:underline" onClick={() => onGo(s)}>
        Change
      </button>
    ) : null
  return (
    <dl className="grid grid-cols-[auto_1fr_auto] gap-x-4 gap-y-2 rounded-lg border border-line p-4 text-sm">
      <dt className="text-ink-muted">Canvass</dt>
      <dd>
        <span className="font-medium">{d.name}</span>
        {d.description ? <span className="block text-ink-muted">{d.description}</span> : null}
      </dd>
      <dd>{edit('basics')}</dd>
      <dt className="text-ink-muted">Ends</dt>
      <dd>
        {d.effective_end_date ?? '—'}
        {d.target_contacts ? ` · target ${d.target_contacts.toLocaleString()} contacts` : ''}
      </dd>
      <dd />
      <dt className="text-ink-muted">Who</dt>
      <dd>
        {describeWho(d)}
        <span className="block tabular-nums text-ink-muted">
          {d.totals.doors.toLocaleString()} houses · {d.totals.voters.toLocaleString()} voters
        </span>
      </dd>
      <dd>{edit('who')}</dd>
      <dt className="text-ink-muted">Questions</dt>
      <dd>Support (Yes / No / Undecided){d.survey ? ` + ${d.survey.title}` : ''}</dd>
      <dd>{edit('questions')}</dd>
      <dt className="text-ink-muted">Scripts</dt>
      <dd>{d.scripts.length ? d.scripts.map((s) => s.title).join(', ') : 'None'}</dd>
      <dd>{edit('basics')}</dd>
    </dl>
  )
}

// ---------------------------------------------------------------------------
// A published canvass: run it from one page.
// ---------------------------------------------------------------------------
function RunCanvass({ d, campaignId }: { d: CanvassDetail; campaignId: string }) {
  const client = useQueryClient()
  const { campaign } = useSelectedCampaign()
  const [error, setError] = useState<string | null>(null)
  const [section, setSection] = useState<'walkers' | 'questions' | 'details'>('walkers')
  const act = useMutation({
    mutationFn: (action: 'publish' | 'deactivate') => apiPost(`/campaigns/${campaignId}/canvasses/${d.canvass_id}/${action}`),
    onSuccess: async () => {
      setError(null)
      await client.invalidateQueries({ queryKey: ['canvass', d.canvass_id] })
      await client.invalidateQueries({ queryKey: ['canvasses', campaignId] })
    },
    onError: (caught) => setError(message(caught)),
  })
  return (
    <div>
      <Header title={d.name} sub={`${STATE_LABEL[d.state]} · ends ${d.effective_end_date ?? '—'}`} />
      <div className="mt-4 flex flex-wrap gap-2">
        {d.state === 'published' ? (
          <button type="button" className={ui.secondary} disabled={act.isPending} onClick={() => act.mutate('deactivate')}>
            Deactivate
          </button>
        ) : d.state === 'deactivated' ? (
          <button type="button" className={ui.secondary} disabled={act.isPending} onClick={() => act.mutate('publish')}>
            Activate again
          </button>
        ) : null}
      </div>
      <Alert text={error} />
      <div className="mt-5">
        <Summary d={d} />
      </div>
      <p className="mt-3 text-xs text-ink-muted">
        Stats for this canvass come with the results step. Who a published canvass covers cannot change; start a new
        canvass for more precincts.
      </p>

      <div role="tablist" className="mt-6 flex flex-wrap gap-1 border-b border-line">
        {(
          [
            ['walkers', 'Walkers'],
            ['questions', 'Questions'],
            ['details', 'Details'],
          ] as const
        ).map(([k, l]) => (
          <button key={k} type="button" role="tab" aria-selected={section === k} onClick={() => setSection(k)}
            className={section === k ? '-mb-px border-b-2 border-brand-700 px-3 py-2 text-sm font-semibold' : 'px-3 py-2 text-sm text-ink-muted hover:text-ink'}>
            {l}
          </button>
        ))}
      </div>
      {section === 'walkers' ? (
        <WalkersStep d={d} campaignId={campaignId} />
      ) : section === 'questions' ? (
        <QuestionsStep d={d} campaignId={campaignId} />
      ) : (
        <BasicsForm campaignId={campaignId} electionDate={campaign?.election_date ?? null} d={d} onSaved={() => setSection('walkers')} />
      )}
    </div>
  )
}
